import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { openPdf } from '../utils/pdf';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const CAT: Record<string, string> = {
  leden_admin: 'Leden / Admin', kredietopinies: 'Kredietopinies', verzekering: 'Verzekering',
  schade: 'Schade', incasso: 'Incasso', facturen: 'Facturen / Premie',
};
const MAPPEN: { key: string; label: string }[] = [
  { key: 'ontvangen', label: 'Postvak IN' },
  { key: 'concepten', label: 'Concepten' },
  { key: 'verzonden', label: 'Verzonden' },
  { key: 'archief', label: 'Archief' },
];

export const AdminBerichtenPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [map, setMap] = useState('ontvangen');
  const [cat, setCat] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [tel, setTel] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<any>(null);     // geopend bericht-detail {bericht, thread}
  const [compose, setCompose] = useState(false);

  const laad = useCallback(() => {
    setLoading(true);
    const q = `map=${map}${cat ? `&categorie=${cat}` : ''}`;
    fetch(`${API}/api/admin/berichten?${q}`, { headers }).then((r) => (r.ok ? r.json() : [])).then(setRows).finally(() => setLoading(false));
    fetch(`${API}/api/admin/berichten/tellingen`, { headers }).then((r) => (r.ok ? r.json() : {})).then(setTel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, cat, accessToken]);

  useEffect(() => { if (!isAuthenticated || !accessToken) { navigate('/login'); return; } laad(); }, [isAuthenticated, accessToken, navigate, laad]);

  const openDetail = async (id: string) => {
    const r = await fetch(`${API}/api/admin/berichten/${id}`, { headers });
    if (r.ok) { setOpen(await r.json()); laad(); }
  };

  return (
    <Layout>
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">Berichten met leden, per onderdeel. Concepten en verzonden items worden bewaard; verzonden berichten verschijnen in het ledenportaal (en als e-mail zodra die is ingesteld).</p>
        <button onClick={() => setCompose(true)} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">+ Nieuw bericht</button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {MAPPEN.map((m) => {
          const badge = m.key === 'ontvangen' ? tel.ongelezen : m.key === 'concepten' ? tel.concepten : 0;
          return (
            <button key={m.key} onClick={() => setMap(m.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border ${map === m.key ? 'bg-mfb-blue text-white border-mfb-blue' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
              {m.label}{badge ? <span className="ml-1.5 rounded-full bg-mfb-orange text-white text-[10px] px-1.5 py-0.5">{badge}</span> : null}
            </button>
          );
        })}
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="ml-2 px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white">
          <option value="">Alle onderdelen</option>
          {Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen berichten in deze map.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {rows.map((b) => (
            <button key={b.id} onClick={() => openDetail(b.id)} className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center gap-3">
              {map === 'ontvangen' && !b.gelezen && <span className="h-2 w-2 rounded-full bg-mfb-orange shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className={`truncate ${map === 'ontvangen' && !b.gelezen ? 'font-semibold text-mfb-blue' : 'text-slate-800'}`}>{b.onderwerp}</div>
                <div className="text-xs text-slate-400 truncate">{b.afzender_naam} · {b.categorie_label}{b.heeft_bijlage ? ' · 📎 bijlage' : ''}</div>
              </div>
              <div className="text-xs text-slate-400 shrink-0">{b.verzonden_op || b.aangemaakt}</div>
            </button>
          ))}
        </div>
      )}

      {open && <BerichtDetail data={open} map={map} token={accessToken!} headers={headers} onClose={() => setOpen(null)} onChanged={() => { setOpen(null); laad(); }} notify={notify} />}
      {compose && <Compose headers={headers} onClose={() => setCompose(false)} onDone={() => { setCompose(false); laad(); }} notify={notify} />}
    </Layout>
  );
};

const BerichtDetail: React.FC<any> = ({ data, map, token, headers, onClose, onChanged, notify }) => {
  const b = data.bericht;
  const thread = data.thread || [b];
  const [antwoord, setAntwoord] = useState('');
  const [busy, setBusy] = useState(false);
  const [arch, setArch] = useState(false);

  const archiveer = async () => {
    setArch(true);
    try {
      const r = await fetch(`${API}/api/admin/berichten/${b.id}/archiveer`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ gearchiveerd: map !== 'archief' }),
      });
      if (!r.ok) throw new Error();
      notify.success(map === 'archief' ? 'Teruggezet uit archief.' : 'Bericht gearchiveerd.');
      onChanged();
    } catch { notify.error('Mislukt.'); } finally { setArch(false); }
  };

  const stuurAntwoord = async () => {
    if (!antwoord.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/berichten`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: b.member_id, categorie: b.categorie, onderwerp: `Re: ${b.onderwerp}`, body: antwoord, status: 'verzonden', thread_id: b.thread_id }),
      });
      if (!r.ok) throw new Error();
      notify.success('Antwoord verzonden naar het lid.');
      onChanged();
    } catch { notify.error('Verzenden mislukt.'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-lg font-semibold text-mfb-blue">{b.onderwerp}</h2>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={archiveer} disabled={arch}
              className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              {map === 'archief' ? 'Terugzetten' : 'Archiveren'}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-4">{CAT[b.categorie] || b.categorie}</p>
        <div className="space-y-3">
          {thread.map((t: any) => (
            <div key={t.id} className={`rounded-xl border p-3 ${t.van === 'backoffice' ? 'border-mfb-blue/20 bg-mfb-blue/5' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span className="font-medium">{t.van === 'backoffice' ? 'Backoffice' : t.afzender_naam}</span>
                <span>{t.verzonden_op || t.aangemaakt}</span>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-line">{t.body}</div>
              {t.handtekening && <div className="mt-2 text-xs text-slate-400 whitespace-pre-line">{t.handtekening}</div>}
              {t.heeft_bijlage && (
                <button onClick={() => openPdf(`/api/admin/berichten/${t.id}/bijlage`, token)} className="mt-2 text-xs font-medium text-mfb-blue hover:underline">📎 {t.bijlage_titel || 'Bijlage'} (PDF)</button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-slate-100 pt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Antwoord</label>
          <textarea value={antwoord} onChange={(e) => setAntwoord(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Typ een antwoord aan het lid…" />
          <div className="flex justify-end mt-2">
            <button onClick={stuurAntwoord} disabled={busy || !antwoord.trim()} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">{busy ? 'Verzenden…' : 'Antwoord verzenden'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Compose: React.FC<any> = ({ headers, onClose, onDone, notify }) => {
  const [colls, setColls] = useState<any[]>([]);
  const [cid, setCid] = useState('');
  const [leden, setLeden] = useState<any[]>([]);
  const [mid, setMid] = useState('');
  const [cat, setCat] = useState('leden_admin');
  const [onderwerp, setOnderwerp] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => { fetch(`${API}/api/settlement/collectivities-list`, { headers }).then((r) => r.ok ? r.json() : []).then((d) => { setColls(d); if (d[0]) setCid(d[0].id); }); }, []); // eslint-disable-line
  useEffect(() => {
    if (!cid) return;
    fetch(`${API}/api/admin/toegankelijkheid?collectivity_id=${cid}`, { headers }).then((r) => r.ok ? r.json() : []).then((d) => { setLeden(d); setMid(d[0]?.id || ''); });
  }, [cid]); // eslint-disable-line

  const verstuur = async (status: 'concept' | 'verzonden') => {
    if (!mid || !onderwerp.trim()) { notify.error('Kies een lid en vul een onderwerp in.'); return; }
    setBusy(status);
    try {
      const r = await fetch(`${API}/api/admin/berichten`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: mid, categorie: cat, onderwerp, body, status }),
      });
      if (!r.ok) throw new Error();
      notify.success(status === 'verzonden' ? 'Bericht verzonden naar het lid.' : 'Concept opgeslagen.');
      onDone();
    } catch { notify.error('Mislukt.'); } finally { setBusy(''); }
  };

  const inp = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-mfb-blue mb-4">Nieuw bericht</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Collectief</label>
              <select value={cid} onChange={(e) => setCid(e.target.value)} className={`${inp} bg-white`}>{colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Lid</label>
              <select value={mid} onChange={(e) => setMid(e.target.value)} className={`${inp} bg-white`}>{leden.map((m) => <option key={m.id} value={m.id}>{m.company_name}</option>)}</select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Onderdeel</label>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className={`${inp} bg-white`}>{Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Onderwerp</label>
            <input value={onderwerp} onChange={(e) => setOnderwerp(e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Bericht</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className={inp} />
            <p className="mt-1 text-xs text-slate-400">De standaard-ondertekening wordt automatisch toegevoegd bij verzenden.</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuleren</button>
          <button onClick={() => verstuur('concept')} disabled={!!busy} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Opslaan als concept</button>
          <button onClick={() => verstuur('verzonden')} disabled={!!busy} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">{busy === 'verzonden' ? 'Verzenden…' : 'Verzenden'}</button>
        </div>
      </div>
    </div>
  );
};

export default AdminBerichtenPage;
